import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChartWidget } from '@/components/ui/shared/ChartWidget';

/**
 * ListChartWidget - Functional component for List-only widget
 * 
 * Replaces the class-based ListChart component with modern React patterns
 * This widget only shows a list view (no chart toggle)
 */
function ListChartWidget({ data, handlechangea, _Index, hideLegend = false }) {
    // State - only show loader if data is ready to fetch
    const [listData, setListData] = useState([]);
    const [listHeaders, setListHeaders] = useState([]);
    const [legendValue, setLegendValue] = useState('');
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState('');

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Get user data
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Export widget data as CSV
    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data.title || 'export';
            const tableData = [];
            
            // Build CSV from list data
            if (listHeaders.length > 0) {
                tableData.push(listHeaders.join(','));
            }
            
            listData.forEach(row => {
                const rowData = row.map((cell, cellIndex) => {
                    let cellValue = String(cell || '').split(',').join('');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {

        }
    }, [data.title, listData, listHeaders]);

    // Map display column names to backend filter keys
    const getBackendKeyForColumn = useCallback((columnHeader) => {
        // Mapping for common column headers to backend keys
        const mapping = {
            // Pharmacy columns
            'Name': 'PharmacyName',
            'Pharmacy Name': 'PharmacyName', // For Patient Savings widget
            'City': 'PhCity',
            'State': 'PhState',
            'Zip(*)': 'PhZip',
            
            // Prescriber columns
            'Prescriber Name': 'Prescriber',
            'Prescriber Address': 'PrAddress',
            'Prescriber City': 'PrCity',
            'Prescriber State': 'PrState',
            'Prescriber Zip': 'PrZip',
            
            // Wholesaler/Distributor columns
            'Wholesaler Name': 'WholesalerName',
            'Distributor Name': 'DistributorName',
            'Group Number': 'GroupNumber',
            
            // Drug columns
            'Drug Name': 'DrugName',
            
            // Keep these as-is if they appear
            'PharmacyName': 'PharmacyName',
            'Prescriber': 'Prescriber',
            'PrCity': 'PrCity',
            'PrState': 'PrState',
            'PrZip': 'PrZip',
            'PhCity': 'PhCity',
            'PhState': 'PhState',
            'PhZip': 'PhZip',
            'DrugName': 'DrugName',
            'WholesalerName': 'WholesalerName',
            'DistributorName': 'DistributorName',
            'GroupNumber': 'GroupNumber'
        };
        
        return mapping[columnHeader] || columnHeader;
    }, []);

    // Handle list item click (for filtering)
    const handleListItemClick = useCallback((row, rowIdx, cellIdx = 0) => {
        if (handlechangea && listHeaders.length > 0) {
            const columnHeader = listHeaders[cellIdx];
            
            // Map the display column name to backend key
            const backendKey = getBackendKeyForColumn(columnHeader);
            
            // Only create filter if we have a valid heading
            if (!backendKey || backendKey === 'undefined') {
                return;
            }
            
            const filterData = {
                heading: backendKey,
                value: row[cellIdx],
            };
            
            handlechangea(filterData);
        }
    }, [handlechangea, listHeaders, getBackendKeyForColumn]);

    // Fetch data
    const fetchData = useCallback(async () => {
        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (!apidata || !apidata[0]) {
                setNoDataMessage('No data available');
                setIsLoading(false);
                return;
            }

            // Process legend data
            try {
                if (apidata[0].legend?.length > 0) {
                    setLegendValue(apidata[0].legend[0].legendValue);
                } else {
                    setLegendValue('');
                }
            } catch {
                setLegendValue('');
            }

            // Process list data
            try {
                const listDataSet = apidata[0].listDataSet;
                if (listDataSet && listDataSet.length > 0) {
                    // First row is headers
                    const headers = listDataSet[0]?.datasets?.map(d => d.data) || [];
                    setListHeaders(headers);

                    // Check for data
                    const hasData = listDataSet.length > 1;
                    if (!hasData) {
                        setNoDataMessage('No Data Available');
                    } else {
                        setNoDataMessage('');
                    }

                    // Rest is data
                    const rows = listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    setListData(rows);
                } else {
                    setNoDataMessage('No Data Available');
                }
            } catch (err) {

                setNoDataMessage('Error processing data');
            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') {
                return; // Expected cancel
            }

            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data.url, data.RequestJson, data.RequestJson1, handleResponse]);

    // Fetch data when RequestJson changes (with null guard)
    useEffect(() => {
        if (data.type === 'List' && data.RequestJson && data.url && 
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Initial fetch (with null guard and proper deps)
    useEffect(() => {
        if (data.type === 'List' && data.RequestJson && data.url) {
            fetchData();
        }
        
        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data.type, data.RequestJson, data.url, fetchData]);

    return (
        <ChartWidget
            title={data.title}
            subtitle={data.Subtitle}
            legendValue={!hideLegend && userdata.userrole !== 'SubAdmin' ? legendValue : null}
            isLoading={isLoading}
            error={error}
            noDataMessage={noDataMessage || 'No data available'}
            chartContent={null}
            listData={listData}
            listHeaders={listHeaders}
            onListItemClick={handlechangea ? handleListItemClick : undefined}
            onExport={handleExport}
            exportTitle={data.title}
            showToggle={false}
            defaultView="list"
            minHeight="300px"
        />
    );
}

export default ListChartWidget;

